import { Controller } from 'stimulus';
import { FocusTrap } from 'shared/assets/javascript/accessibility/focusTrap.js';
import Swiper, { A11y, Lazy, Navigation, Pagination } from 'swiper';
Swiper.use([A11y, Lazy, Navigation, Pagination]);

export default class extends Controller {
  static targets = [
    'cta',
    'close',
    'carousel',
    'carouselWrapper',
    'controls',
    'links',
    'caption'
  ];

  get closeButton() {
    return document.querySelector('.gallery-overlay__close-button');
  }

  get ctaButton() {
    return this.ctaTarget?.querySelector('.btn__cta') || null;
  }

  get galleryGridAssets() {
    return document.querySelectorAll('.-gallery-overlay-asset');
  }

  connect() {
    this.state = {
      open: false,
      video: false,
      playing: false,
      isTouch: Site.environment.touch,
      isMobile: Site.environment.isMobile,
      defaultCtaUrl: this.hasCtaTarget ? this.ctaButton?.getAttribute('href') : '',
      defaultCtaTitle:this.hasCtaTarget ?  this.ctaTarget.textContent : '',
      clickedItem: null,
    };
    const modal = document.querySelector('.gallery-overlay');
    const modalToggle = this.closeButton;
    this.focusTrapInstance = new FocusTrap([modalToggle, modal]);

    this.boundOpen = this.open.bind(this);
    this.boundClose = this.close.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    this.setupEvents();
  }

  disconnect() {
    this.removeEvents();
    this.removeCarousel();
  }

  setupEvents() {
    this.galleryGridAssets.forEach(asset => asset.addEventListener('click', this.boundOpen));
    this.closeButton.addEventListener('click', this.boundClose);
    document.addEventListener('keydown', this.handleKeydown);
  }

  removeEvents() {
    this.galleryGridAssets.forEach(asset => asset.removeEventListener('click', this.boundOpen));
    this.closeButton.removeEventListener('click', this.boundClose);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleKeydown(event) {
    if (event.key !== 'Escape' || !this.state.open) return;

    event.preventDefault();
    this.close();
  }

  open(event) {
    const clicked = event.target.closest('.-gallery-overlay-asset');
    if (!clicked || clicked.dataset.opens !== 'gallery-overlay') return;

    event.preventDefault();

    if (this.state.open) {
      this.removeCarousel();
    }

    this.state.clickedItem = clicked;
    const galleryItems = document.querySelector('.gallery-card') ?
      document.querySelectorAll('.js-gallery-grid-container:not(.-is-hidden) .-gallery-overlay-asset') :
      document.querySelectorAll('.-gallery-overlay-asset');
    const galleryHtml = this.getGalleryHtml(galleryItems);
    const galleryIndex = [...galleryItems].indexOf(clicked);
    this.element.setAttribute('aria-hidden', 'false');
    this.carouselWrapperTarget.innerHTML = galleryHtml;
    document.body.classList.add('-hide-page-overflow');
    this.focusTrapInstance.set();
    this.closeButton.focus();
    this.state.open = true;
    if (galleryItems.length) {
      requestAnimationFrame(() => {
        this.initCarousel(galleryIndex);
        this.lazyLoadCarouselAssets();
      })
    }
  }

  close(event) {
    if (event) event.preventDefault();
    document.body.classList.remove('-hide-page-overflow');
    this.focusTrapInstance.unset();
    this.state.clickedItem.focus();
    this.state.clickedItem = null;
    this.element.setAttribute('aria-hidden', 'true');
    this.element.removeAttribute('inert', '');
    this.removeCarousel();
    this.clearVideo();
    this.state.open = false;
  }

  getGalleryHtml(items) {
    const renderVideo = !this.state.isMobile && !this.state.isTouch;
    return Array.from(items).map(item => {
      const ctaUrl = item.getAttribute('data-cta-url') || '';
      const ctaTitle = item.getAttribute('data-cta-title') || '';
      let innerHtml = item.outerHTML;
      const videoUrl = item.querySelector('[data-video_url]')?.getAttribute('data-video_url');
      const assetType = item.querySelector('video') ? 'video' : (item.querySelector('.asset-holder__image--embed') ? 'embed' : 'image');
      const image = item.querySelector('img');
      const src = this.getImageSrc(image);
      const alt = image.getAttribute('alt');
      const caption = image.parentElement.querySelector('.asset-holder__image--caption')?.textContent || '';
      if (assetType === 'video' && renderVideo) {
        innerHtml = item.querySelector('video').outerHTML;
        return `
          <div class='gallery-overlay__media swiper-slide' role='group'>
            <div class='gallery-overlay__video' data-caption="${caption}" data-cta-url='${ctaUrl}' data-cta-title='${ctaTitle}'>
              <div class='gallery-overlay__video-container'>
                ${innerHtml}
              </div>
            </div>
          </div>
        `;
      } else if (assetType === 'embed') {
        if (videoUrl) {
          return `
            <div class='gallery-overlay__media swiper-slide' role='group'>
              <div class='gallery-overlay__video-container' data-cta-url='${ctaUrl}' data-cta-title='${ctaTitle}'>
                <iframe class='gallery-overlay__embed' src="${videoUrl}" data-video_url="${videoUrl}" title='gallery video'>
                </iframe>
              </div>
            </div>
          `;
        }
      } else {
        if (src) {
          return `
            <div class='gallery-overlay__media swiper-slide' role='group'>
              <img class='gallery-overlay__image' src='${src}' alt='${alt}' data-caption="${caption}" data-cta-url='${ctaUrl}' data-cta-title='${ctaTitle}' />
            </div>
          `;
        }
      }
    }).join('');
  }

  getImageSrc(image) {
    if (this.state.isMobile) {
      return image.getAttribute('data-gallery') || image.getAttribute('src') || image.getAttribute('data-lazy_src') || false;
    } else {
      return image.getAttribute('data-gallery-x2000') || image.getAttribute('src') || image.getAttribute('data-lazy_src') || false;
    }
  }

  initCarousel(index) {
    this.swiper = new Swiper(this.carouselTarget, {
      loop: true,
      slidesPerView: 1,
      spaceBetween: 0,
      lazy: true,
      preloadImages: false,
      initialSlide: index,
      navigation: {
        prevEl: '.gallery-overlay__prev',
        nextEl: '.gallery-overlay__next',
      },
      a11y: {
        liveRegion: false,
        slideLabelMessage: 'Slide {{index}} of {{slidesLength}}'
      },
      on: {
        afterInit: (swiper) => {
          this.setCta(swiper);
          this.setVideo(swiper);
          this.updateInert(swiper);
        },

        slideChange: (swiper) => {
          this.setCta(swiper);
          this.setCaption(swiper);
          this.setVideo(swiper);
          this.updateInert(swiper);
        },

        lazyImageReady: () => {
          this.carouselTarget.trigger('scroll');
        },
      }
    });
  }

  removeCarousel() {
    this.carouselWrapperTarget.innerHTML = '';
  }

  lazyLoadCarouselAssets() {
    document.dispatchEvent(new Event('scroll'));
  }

  setVideo(swiper) {
    const slide = swiper.slides[swiper.activeIndex];
    const video = slide.querySelector('video') || false;
    const hasVideo = video.length || false;
    if (hasVideo) {
      this.state.video = video;
      this.toggleVideo();
    } else {
      this.toggleVideo();
      this.state.video = false;
    }
  }

  updateInert(swiper) {
    const activeSlide = swiper.slides[swiper.activeIndex];
    swiper.slides.forEach(slide => {
      if (slide === activeSlide) {
        slide.removeAttribute('inert');
        return;
      }

      slide.setAttribute('inert', '');
    })
  }

  setCaption(swiper) {
    const slide = swiper.slides[swiper.activeIndex];
    const caption = slide.querySelector('[data-caption]')?.getAttribute('data-caption') || '';
    if (this.hasCaptionTarget) {
      this.captionTarget.textContent = caption;
    }
  }

  setCta(swiper) {
    const slide = swiper.slides[swiper.activeIndex];
    const ctaUrl = slide.querySelector('[data-cta-url]')?.getAttribute('data-cta-url') || this.state.defaultCtaUrl;
    const ctaTitle = slide.querySelector('[data-cta-title]')?.getAttribute('data-cta-title') || this.state.defaultCtaTitle;
    if (this.hasCtaTarget && !!this.ctaButton) {
      this.ctaButton.textContent = ctaTitle;
      this.ctaButton.setAttribute('href', ctaUrl);
    }
  }

  toggleVideo() {
    if (this.state.video && this.state.playing) {
      this.pauseVideo();
    } else if (this.state.video && !this.state.playing) {
      this.playVideo();
    }
  }

  playVideo() {
    if (this.state.video) {
      this.state.video.play();
    }
    this.state.playing = true;
  }

  pauseVideo() {
    if (this.state.video) {
      this.state.video.pause();
    }
    this.state.playing = false;
  }

  clearVideo() {
    this.state.video = false;
    this.state.playing = false;
  }

  setEmbed(swiper) {
    const slide = swiper.slides[swiper.activeIndex];
    const embed = slide.querySelector('iframe') || false;
    const hasEmbed = embed.length || false;
    if (hasEmbed) {
      const videoUrl = embed.getAttribute('data-video_url');
      document.querySelector('.gallery-overlay__embed').setAttribute('src', '');
      embed.setAttribute('src', videoUrl);
    } else {
      document.querySelector('.gallery-overlay__embed').setAttribute('src', '');
    }
  }
}

